import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Typography,
  Button,
  Space,
  message,
  Spin,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Pagination,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import "./styles.css";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

// Local Device interface definition
interface Device {
  id: number;
  deviceCode: string;
  deviceName: string;
  data: any;
  isPay: number;
  project: number | { id: number; name: string };
  addTime: string;
  deviceType: number;
  thirdType: string;
  area: number | { id: number; title: string };
  beian: string;
  exData: {
    driverName?: string;
    idcardNumber?: string;
    monitorKey?: string;
    channel?: any[];
    [key: string]: any;
  };
  deviceFactory: string;
  latitude: string;
  longitude: string;
  uid: string | null;
  price: number | null | string;
  isDel: number;
  remark: string | null;
}

// Local deviceTypeMap definition
const deviceTypeMap: Record<number, string> = {
  0: "塔吊",
  1: "扬尘",
  3: "视频",
  4: "卸料平台",
  5: "施工升降机",
};

// Define API response interface with pagination structure
interface ApiResponseWithPagination<T> {
  code: number;
  msg: string;
  data?: {
    items: T[];
    total: number;
    page: number;
    limit: number;
  };
}

const DeviceQuery: React.FC = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedDeviceType, setSelectedDeviceType] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch devices with pagination
  const fetchDevices = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      
      // Prepare query parameters
      const params: Record<string, any> = {
        page,
        limit,
      };
      
      // Add deviceType filter if selected
      if (selectedDeviceType !== null) {
        params.deviceType = selectedDeviceType;
      }
      
      // Add search value if exists
      if (searchValue) {
        params.search = searchValue;
      }
      
      const response = await api.get<ApiResponseWithPagination<Device>>('/devices', { params });
      
      if (response.data.code === 0 && response.data.data) {
        const { items, total } = response.data.data;
        setDevices(items);
        setPagination({
          ...pagination,
          current: page,
          total,
        });
      } else {
        message.error("获取设备列表失败");
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      message.error("获取设备列表时出错");
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchDevices();
  }, []);
  
  // Handle search
  const handleSearch = (value: string) => {
    setSearchValue(value);
    fetchDevices(1, pagination.pageSize); // Reset to first page when searching
  };
  
  // Handle device type filter change
  const handleDeviceTypeChange = (value: number | null) => {
    setSelectedDeviceType(value);
    fetchDevices(1, pagination.pageSize); // Reset to first page when changing filter
  };
  
  // Handle pagination change
  const handlePageChange = (page: number, pageSize?: number) => {
    fetchDevices(page, pageSize || pagination.pageSize);
  };
  
  // Handle reset filters
  const handleResetFilters = () => {
    setSearchValue("");
    setSelectedDeviceType(null);
    fetchDevices(1, pagination.pageSize);
  };
  
  // Table columns
  const columns = [
    {
      title: "设备ID",
      dataIndex: "deviceCode",
      key: "deviceCode",
      render: (text: string) => <a>{text}</a>,
    },
    {
      title: "设备名称",
      dataIndex: "deviceName",
      key: "deviceName",
    },
    {
      title: "设备类型",
      dataIndex: "deviceType",
      key: "deviceType",
      render: (type: number) => (
        <Tag color="blue">{deviceTypeMap[type] || "未知类型"}</Tag>
      ),
    },
    {
      title: "所属项目",
      key: "project",
      render: (_: any, record: Device) => {
        const projectName = 
          typeof record.project === 'object' && record.project !== null
            ? record.project.name
            : '未知项目';
        const projectId = typeof record.project === 'object' && record.project !== null
          ? record.project.id
          : record.project;
        
        return (
          <a 
            onClick={() => navigate(`/devices/${projectId}`, { 
              state: { 
                projectName: typeof record.project === 'object' ? record.project.name : projectName
              } 
            })}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {projectName}
          </a>
        );
      },
    },
    {
      title: "添加时间",
      dataIndex: "addTime",
      key: "addTime",
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: "支付状态",
      key: "isPay",
      render: (_: any, record: Device) => (
        <Tag color={record.isPay === 1 ? "green" : "red"}>
          {record.isPay === 1 ? "已支付" : "未支付"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "action",
      render: (_: any, record: Device) => {
        const projectId = typeof record.project === 'object' && record.project !== null
          ? record.project.id
          : record.project;
          
        return (
          <Space size="middle">
            <Button 
              type="link" 
              onClick={() => window.open(`/devices/${projectId}`, '_blank')}
            >
              查看详情
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="device-query-container">
      <Card>
        <Title level={4}>设备查询</Title>
        <div className="filter-section">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8} md={6}>
              <Search
                placeholder="搜索设备ID或名称"
                onSearch={handleSearch}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={8} md={5}>
              <Select
                placeholder="选择设备类型"
                value={selectedDeviceType}
                onChange={handleDeviceTypeChange}
                allowClear
                style={{ width: "100%" }}
              >
                {Object.entries(deviceTypeMap).map(([key, value]) => (
                  <Option key={key} value={parseInt(key)}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={13}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilters}
                >
                  重置筛选
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => fetchDevices(pagination.current, pagination.pageSize)}
                >
                  查询
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={devices}
            rowKey="id"
            size="small"
            pagination={false}
            className="device-table"
          />
          <div className="pagination-container">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `共 ${total} 条记录`}
            />
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default DeviceQuery;
